/** Shared type declarations for the synthesizer. */

import type {
	WasmEnvelopeNode,
	WasmFilterNode,
	WasmOscillatorNode,
} from "../initAudio.ts";

/** Supported oscillator waveform types. */
export type WaveformType =
	| "sine"
	| "sawtooth"
	| "square"
	| "triangle"
	| "wavetable";

/** Numeric value mapped to each waveform for the AudioParam. */
export type WaveformValue = 0 | 1 | 2 | 3 | 4;

/** ADSR envelope parameters. */
export type ADSRParams = {
	readonly attack: number;
	readonly decay: number;
	readonly sustain: number;
	readonly release: number;
};

/** Filter parameters. */
export type FilterParams = {
	readonly cutoff: number;
	readonly resonance: number;
};

/** Default ADSR envelope. */
export const DEFAULT_ADSR: ADSRParams = {
	attack: 0.01,
	decay: 0.1,
	sustain: 0.7,
	release: 0.3,
} as const;

/** Default filter parameters. */
export const DEFAULT_FILTER: FilterParams = {
	cutoff: 2000,
	resonance: 1.0,
} as const;

/** A single oscillator layer with its own waveform, pitch, envelope, and filter. */
export type OscillatorLayer = {
	readonly id: string;
	readonly waveform: WaveformType;
	/** Semitone detune offset for this layer (−24 to +24). */
	readonly semitoneOffset: number;
	/** Cent detune offset for this layer (−100 to +100). */
	readonly centOffset: number;
	/** Relative volume of this layer (0 to 1). */
	readonly gain: number;
	/** Stereo pan position for this layer (−1 = left, 0 = center, +1 = right). */
	readonly pan: number;
	/** Per-layer ADSR envelope. */
	readonly adsr: ADSRParams;
	/** Per-layer filter. */
	readonly filter: FilterParams;
	/** Whether this layer is active. Disabled layers produce no sound. */
	readonly enabled: boolean;
	/** Selected wavetable id when waveform === "wavetable". */
	readonly wavetableId?: string;
	/** Raw PCM samples for a custom user-loaded wavetable (waveform === "wavetable", wavetableId === "custom"). */
	readonly customWavetable?: readonly number[];
};

/** Default oscillator layer. */
export const DEFAULT_LAYER: OscillatorLayer = {
	id: "layer-1",
	waveform: "sawtooth",
	semitoneOffset: 0,
	centOffset: 0,
	gain: 1.0,
	pan: 0,
	adsr: DEFAULT_ADSR,
	filter: DEFAULT_FILTER,
	enabled: true,
} as const;

/** WASM wrapper references that must stay alive while the voice is active.
 *  Dropping these (via GC or `.free()`) sets `is_active = false` in the Rust
 *  `AudioWorkletNodeWrapper`, which causes `process()` to return `false` and
 *  permanently deactivates the AudioWorklet processor.
 *  `oscillatorRef` is null for wavetable voices that use a native OscillatorNode.
 */
export type VoiceWasmRefs = {
	readonly oscillatorRef: WasmOscillatorNode | null;
	readonly filterRef: WasmFilterNode;
	readonly envelopeRef: WasmEnvelopeNode;
};

/** A single layer instance within a voice (one per oscillator layer). */
export type VoiceLayer = {
	readonly oscillator: AudioNode;
	readonly filter: AudioWorkletNode;
	readonly envelope: AudioWorkletNode;
	readonly gain: GainNode;
	readonly panner: StereoPannerNode;
	readonly wasmRefs: VoiceWasmRefs;
	readonly layerId: string;
	/** Native Web Audio oscillator used for wavetable voices (null otherwise). */
	readonly nativeOsc: OscillatorNode | null;
};

/** A single synth voice — may contain multiple layers for fat sound. */
export type Voice = {
	readonly layers: readonly VoiceLayer[];
	readonly voiceGain: GainNode;
	readonly note: number;
	readonly startTime: number;
	/** Per-note cent offset (e.g. just intonation adjustment). */
	readonly extraCents: number;
};

/** Global synth parameters — per-layer settings live in each OscillatorLayer. */
export type SynthParams = {
	readonly masterGain: number;
	/** Stereo pan position for the overall synth output (−1 = left, 0 = center, +1 = right). */
	readonly pan: number;
	readonly oscillatorLayers: readonly OscillatorLayer[];
	/** Global tempo in BPM, used for tempo-synced effects. */
	readonly tempo: number;
};

/** Maps waveform type names to their AudioParam values. */
export const WAVEFORM_VALUES: Readonly<Record<WaveformType, WaveformValue>> = {
	sine: 0,
	sawtooth: 1,
	square: 2,
	triangle: 3,
	wavetable: 4,
} as const;

/** Default synth parameters. */
export const DEFAULT_PARAMS: SynthParams = {
	masterGain: 0.7,
	pan: 0,
	oscillatorLayers: [DEFAULT_LAYER],
	tempo: 120,
} as const;

/** Maximum number of simultaneous voices (per-note, not per-layer). */
export const MAX_POLYPHONY = 8 as const;

/** Maximum number of oscillator layers allowed. */
export const MAX_LAYERS = 8 as const;

// ---- Delay Effect Types ----

/** Musical note division for tempo-synced delay. */
export type NoteDivision =
	| "1/1"
	| "1/2"
	| "1/2d"
	| "1/4"
	| "1/4d"
	| "1/8"
	| "1/8d"
	| "1/16"
	| "1/16d"
	| "1/4t"
	| "1/8t";

/** Multiplier for each note division relative to one beat. */
export const NOTE_DIVISION_MULTIPLIERS: Readonly<Record<NoteDivision, number>> =
	{
		"1/1": 4.0,
		"1/2": 2.0,
		"1/2d": 3.0,
		"1/4": 1.0,
		"1/4d": 1.5,
		"1/8": 0.5,
		"1/8d": 0.75,
		"1/16": 0.25,
		"1/16d": 0.375,
		"1/4t": 2 / 3,
		"1/8t": 1 / 3,
	} as const;

/** Delay time mode — free (milliseconds) or synced to tempo. */
export type DelayTimeMode = "free" | "sync";

/** Stereo delay effect parameters. */
export type DelayParams = {
	/** Whether the delay effect is active. */
	readonly enabled: boolean;
	/** Delay time in milliseconds (used when mode is "free"). */
	readonly timeMs: number;
	/** Musical note division (used when mode is "sync"). */
	readonly noteDivision: NoteDivision;
	/** Whether time is free (ms) or synced to tempo. */
	readonly timeMode: DelayTimeMode;
	/** Feedback amount (0 to 1). */
	readonly feedback: number;
	/** Dry/wet mix (0 = fully dry, 1 = fully wet). */
	readonly mix: number;
	/** Whether to use ping-pong stereo delay. */
	readonly pingPong: boolean;
	/** Low-pass filter cutoff on the feedback path (Hz). */
	readonly filterCutoff: number;
};

/** Default delay parameters. */
export const DEFAULT_DELAY: DelayParams = {
	enabled: false,
	timeMs: 375,
	noteDivision: "1/8",
	timeMode: "sync",
	feedback: 0.35,
	mix: 0.3,
	pingPong: false,
	filterCutoff: 8000,
} as const;

// ---- Limiter / Compressor Types ----

/** Limiter mode: transparent preserves dynamics, clipper acts as a brick wall. */
export type LimiterMode = "transparent" | "clipper";

/** Limiter / compressor effect parameters. */
export type LimiterParams = {
	/** Whether the limiter is active. */
	readonly enabled: boolean;
	/** Mode: transparent (gentle) or clipper (hard limiting). */
	readonly mode: LimiterMode;
	/** Threshold in dB (−60 to 0). Signals above this are compressed. */
	readonly threshold: number;
	/** Input gain in dB (−24 to +24). Pre-compression boost/cut. */
	readonly inputGain: number;
	/** Output gain in dB (−24 to +24). Post-compression makeup gain. */
	readonly outputGain: number;
};

/** Default limiter parameters. */
export const DEFAULT_LIMITER: LimiterParams = {
	enabled: false,
	mode: "transparent",
	threshold: -12,
	inputGain: 0,
	outputGain: 0,
} as const;

// ---- Multi-Synth Types ----

/** MIDI channel selection — 1–16 or "all" to receive from every channel. */
export type MidiChannel =
	| 1
	| 2
	| 3
	| 4
	| 5
	| 6
	| 7
	| 8
	| 9
	| 10
	| 11
	| 12
	| 13
	| 14
	| 15
	| 16
	| "all";

/** A named synth instance with its own parameters and MIDI routing. */
export type SynthInstance = {
	readonly id: string;
	readonly name: string;
	readonly midiChannel: MidiChannel;
	readonly params: SynthParams;
	readonly delay: DelayParams;
};

/** Maximum number of synth instances. */
export const MAX_SYNTHS = 8 as const;

/** Default synth instance. */
export const DEFAULT_SYNTH_INSTANCE: SynthInstance = {
	id: "synth-1",
	name: "Synth 1",
	midiChannel: "all",
	params: DEFAULT_PARAMS,
	delay: DEFAULT_DELAY,
} as const;
